import { useEffect } from 'react';
import { apiFetch } from '../api/client';
import { collectClientContext } from './collectClientContext';

/** Slightly shorter than server PresenceTtlSeconds (12) so live rosters stay fresh. */
const HEARTBEAT_MS = 10_000;
const RESIZE_DEBOUNCE_MS = 500;
const PRESENCE_PAUSE_PATH = '/api/activity/pause';

export interface UseActivityHeartbeatOptions {
  enabled: boolean;
  themeStorageKey: string;
}

function pausePresence() {
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(PRESENCE_PAUSE_PATH, '');
    return;
  }
  void apiFetch(PRESENCE_PAUSE_PATH, { method: 'POST' }).catch(() => {
    // ignore transient pause failures
  });
}

export function useActivityHeartbeat({ enabled, themeStorageKey }: UseActivityHeartbeatOptions) {
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let resizeTimer: number | undefined;

    const ping = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      const body = collectClientContext(themeStorageKey);
      void apiFetch('/api/activity/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).catch(() => {
        // ignore transient heartbeat failures
      });
    };

    ping();
    const timer = window.setInterval(ping, HEARTBEAT_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') ping();
    };
    const onPageHide = () => {
      pausePresence();
    };
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(ping, RESIZE_DEBOUNCE_MS);
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      pausePresence();
      window.clearInterval(timer);
      window.clearTimeout(resizeTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('resize', onResize);
    };
  }, [enabled, themeStorageKey]);
}
